import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import getLocalizedDateTime from "@insite/client-framework/Common/Utilities/getLocalizedDateTime";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { InvoicesDataViewContext } from "@insite/client-framework/Store/Data/Invoices/InvoicesSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import updateSearchFields from "@insite/client-framework/Store/Pages/InvoiceHistory/Handlers/UpdateSearchFields";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import InvoiceDetailPageTypeLink from "@insite/content-library/Components/InvoiceDetailPageTypeLink";
import Button, { ButtonWrapper } from "@insite/mobius/Button";
import Checkbox, { CheckboxPresentationProps } from "@insite/mobius/Checkbox";
import Clickable, { ClickableProps } from "@insite/mobius/Clickable";
import DataTable, { DataTableProps, SortOrderOptions } from "@insite/mobius/DataTable";
import DataTableBody, { DataTableBodyProps } from "@insite/mobius/DataTable/DataTableBody";
import DataTableCell from "@insite/mobius/DataTable/DataTableCell";
import { DataTableCellBaseProps } from "@insite/mobius/DataTable/DataTableCellBase";
import DataTableHead, { DataTableHeadProps } from "@insite/mobius/DataTable/DataTableHead";
import DataTableHeader, { DataTableHeaderProps } from "@insite/mobius/DataTable/DataTableHeader";
import DataTableRow, { DataTableRowProps } from "@insite/mobius/DataTable/DataTableRow";
import Icon, { IconPresentationProps } from "@insite/mobius/Icon";
import CreditCard from "@insite/mobius/Icons/CreditCard";
import Pocket from "@insite/mobius/Icons/Pocket";
import Printer from "@insite/mobius/Icons/Printer";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import LoadingSpinner, { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { isMgbwInvoiceBrand } from "@tSurya/Common/Utilities/websiteUtils";
import generatePdf from "@tSurya/Handlers/GeneratePdfHandler";
import CustomReturnIcon from "@tSurya/Icons/CustomReturnIcon";
import PlusIcon from "@tSurya/Icons/PlusIcon";
import * as suryaDesign from "@tSurya/theme/design.surya";
import { COLORS, TYPOGRAPHY } from "@tSurya/theme/design.surya";
import { getCurrentDesign } from "@tSurya/theme/design";
import { THEME_IDS, isThemeActive } from "@tSurya/theme/themeHelper";
import MyAccountThemeWrapper from "@tSurya/Utils/MyAccountThemeWrapper";
import SuryaPageThemeWrapper from "@tSurya/Utils/SuryaPageThemeWrapper";
import * as React from "react";
import { useContext } from "react";
import { connect, ResolveThunks } from "react-redux";
import { createGlobalStyle, css } from "styled-components";
import QuestionCircle from "@insite/mobius/Icons/QuestionCircle";
import ProductAvailabilityInfoIcon from "@tSurya/Icons/ProductAvailabilityInfoIcon";
import siteMessage from "@insite/client-framework/SiteMessage";

/**
 * The visible "Invoice History" title is rendered by the generic `PageTitle`
 * widget and the "Balances" heading by the InvoiceHistoryBuckets widget. Both
 * pull their color from the active brand theme (MGBW renders #333333) instead of
 * Surya's dark text (#323132). This page-scoped global style pins them to Surya
 * so the MGBW / Global Views Invoice History page matches Surya exactly.
 *
 * Injected from this table widget because it always renders on the page
 * (loading / empty / results). Scoped to the Invoice History page container and
 * rendered only for non-Surya brands, so Surya styling is never touched. The
 * value also equals what Surya already renders, so it is a no-op even if it ever
 * cascaded there.
 */
const InvoiceHistorySuryaTitleColor = createGlobalStyle`
    [data-test-selector="page_InvoiceHistoryPage"] [data-test-selector="PageTitle"],
    [data-test-selector="page_InvoiceHistoryPage"] .balance {
        color: ${suryaDesign.COLORS.darkmagenta} !important;
    }
`;

const enum fields {
    showInvoiceNumber = "showInvoiceNumber",
    showInvoiceDate = "showInvoiceDate",
    showTerms = "showTerms",
    showDueDate = "showDueDate",
    showShipTo = "showShipTo",
    showPONumber = "showPONumber",
    showStatus = "showStatus",
    showInvoiceTotal = "showInvoiceTotal",
    showCurrentBalance = "showCurrentBalance",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.showInvoiceNumber]: boolean;
        [fields.showInvoiceDate]: boolean;
        [fields.showTerms]: boolean;
        [fields.showDueDate]: boolean;
        [fields.showShipTo]: boolean;
        [fields.showPONumber]: boolean;
        [fields.showStatus]: boolean;
        [fields.showInvoiceTotal]: boolean;
        [fields.showCurrentBalance]: boolean;
    };
}

const mapStateToProps = (state: ApplicationState) => ({
    getInvoicesParameter: state.pages.invoiceHistory.getInvoicesParameter,
    failedToLoadInvoices: state.data.invoices.failedToLoadInvoices,
    language: state.context.session.language,
    rmaLink: getPageLinkByPageType(state, "RequestRmaPage"),
    showPricing: state.context.showPricing,
});

const mapDispatchToProps = {
    updateSearchFields,
    generatePdf,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

interface SelectedInvoice {
    invoiceNumber: string;
    currentBalance: string | number;
    brand?: string;
}

const selectedInvoicesChangedEventName = "SuryaInvoiceHistorySelectedInvoicesChanged";

export interface InvoiceHistoryTableStyles {
    container?: InjectableCss;
    headerClickables?: ClickableProps;
    headerText?: TypographyProps;
    centeringWrapper?: InjectableCss;
    spinner?: LoadingSpinnerProps;
    noResultsContainer?: InjectableCss;
    noResultsText?: TypographyProps;
    dataTable?: DataTableProps;
    dataTableHead?: DataTableHeadProps;
    dataTableHeaders?: DataTableHeaderProps;
    dataTableBody?: DataTableBodyProps;
    dataTableRow?: DataTableRowProps;
    dataTableCells?: DataTableCellBaseProps;
    invoiceNumberHeader?: DataTableHeaderProps;
    invoiceDateHeader?: DataTableHeaderProps;
    termsHeader?: DataTableHeaderProps;
    dueDateHeader?: DataTableHeaderProps;
    shipToHeader?: DataTableHeaderProps;
    poNumberHeader?: DataTableHeaderProps;
    statusHeader?: DataTableHeaderProps;
    totalHeader?: DataTableHeaderProps;
    currentBalanceHeader?: DataTableHeaderProps;
    invoiceNumberCell?: DataTableCellBaseProps;
    invoiceNumberWrapper?: InjectableCss;
    invoiceCheckboxSlot?: InjectableCss;
    unpaidInvoiceCheckbox?: CheckboxPresentationProps;
    invoiceDateCell?: DataTableCellBaseProps;
    termsCell?: DataTableCellBaseProps;
    dueDateCell?: DataTableCellBaseProps;
    shipToCell?: DataTableCellBaseProps;
    poNumberCell?: DataTableCellBaseProps;
    statusCell?: DataTableCellBaseProps;
    link?: LinkPresentationProps;
    totalCell?: DataTableCellBaseProps;
    currentBalanceCell?: DataTableCellBaseProps;
    returnorderStyles?: DataTableCellBaseProps;
    button?: InjectableCss;
    printIcon?: IconPresentationProps;
    returnIcon?: IconPresentationProps;
    payNowIcon?: IconPresentationProps;
    circleIcon?: TypographyProps;
    disableTooltipMessage?: InjectableCss;
}

export const tableStyles: InvoiceHistoryTableStyles = {
    circleIcon: {
        css: css`
            position: relative;
            display: inline-flex;
            align-items: center;
            > svg {
                width: 20px;
                height: 20px;
            }
            > span {
                display: none;
            }
            &:hover > span {
                display: inline-block;
            }
        `,
    },
    disableTooltipMessage: {
        css: css`
            display: none;
            position: absolute;
            right: 13px;
            background: #323132;
            padding: 5px 10px;
            border-radius: 4px;
            z-index: 99;
            font-size: 12px !important;
            color: white;
            font-weight: 600 !important;
            top: -40px;
            white-space: nowrap !important;
        `,
    },
    printIcon: {
        css: css`
            height: 19.25px;
            width: 22px;
            > svg {
                width: 100%;
                height: 100%;
            }
        `,
    },
    returnIcon: {
        css: css`
            height: 16.5px;
            width: 20.62px;
            > svg {
                width: 100%;
                height: 100%;
            }
        `,
    },
    dataTableRow: {
        css: css`
            &:nth-child(even) {
                background: #ffffff !important;
            }
        `,
    },
    container: {
        css: css`
            overflow: auto;
        `,
    },
    centeringWrapper: {
        css: css`
            height: 300px;
            display: flex;
            align-items: center;
        `,
    },
    spinner: {
        css: css`
            margin: auto;
        `,
    },
    noResultsContainer: {
        css: css`
            text-align: center;
            padding: 20px;
        `,
    },
    noResultsText: {
        size: 16,
        weight: 600,
    },
    headerClickables: {
        css: css`
            &:focus {
                outline-style: none;
            }
            width: 100%;
            justify-content: space-between;
            display: block;
        `, // TODO ISC-12604 focus removal is incorrect
    },
    totalHeader: {
        alignX: "right",
    },
    currentBalanceHeader: {
        alignX: "right",
    },
    totalCell: {
        alignX: "right",
        css: css`
            text-align: right !important;
        `,
    },
    currentBalanceCell: {
        alignX: "right",
        css: css`
            text-align: right !important;
        `,
    },
    invoiceNumberWrapper: {
        css: css`
            display: grid;
            grid-template-columns: 22px minmax(0, 1fr);
            align-items: center;
            gap: 10px;
            white-space: nowrap;
        `,
    },
    invoiceCheckboxSlot: {
        css: css`
            display: flex;
            align-items: center;
            justify-content: center;
            width: 22px;
        `,
    },
    unpaidInvoiceCheckbox: {
        css: css`
            line-height: 0;
            span[role="checkbox"] {
                margin: 0;
            }
        `,
    },
    returnorderStyles: {
        css: css`
            [class^="StyledButton"] {
                justify-content: center;
            }
            button {
                &:hover {
                    background: ${COLORS.text.accent};
                    span[class^="TypographyStyle"] {
                        color: ${COLORS.secondary.base};
                    }
                }
            }
        `,
    },
    link: {
        css: css`
            span {
                font-size: 15px;
                color: ${COLORS.text.link} !important;
                &:hover {
                    text-align: center;
                    text-decoration-line: underline;
                }
            }
        `,
    },
    button: {
        css: css`
            display: flex;
            align-items: center;
            padding: 0 36px;
            text-align: center;
            gap: 15px;
            border: 2px solid ${COLORS.text.accent};
            border: none;
            background-color: ${COLORS.primary.contrast};
            &:hover {
                background-color: #ffffff !important;
            }
            &:disabled {
                background-color: #ffffff !important;
            }
            span {
                font-family: ${TYPOGRAPHY.fontFamily.title};
                font-style: normal;
                font-weight: ${TYPOGRAPHY.weight.bold};
                font-size: ${TYPOGRAPHY.size.smaller};
                line-height: ${TYPOGRAPHY.lineHeight.xbase};
                color: ${COLORS.text.main};
                text-align: center;
                background-color: ${COLORS.primary.contrast};
            }
        `,
    },
    dataTable: {
        css: css`
            border: 1px solid ${COLORS.border.main};
            tr {
                > th {
                    width: 124px !important;
                    box-sizing: border-box;
                    height: 60px;
                    white-space: wrap !important;
                    padding: 0px;
                    min-width: 124px !important;
                    &.printColumn {
                        width: 59px !important;
                        padding: 0px;
                        gap: 0px;
                        min-width: 59px !important;
                        &:last-child {
                            span[class^="IconWrapper"] {
                                display: none;
                            }
                        }
                    }
                    &:first-child {
                        width: 165px !important;
                        min-width: 165px !important;
                    }
                }
            }

            tr {
                > td {
                    width: 123px !important;
                    box-sizing: border-box;
                    height: 54px;
                    &.printColumn {
                        width: 59px !important;
                        padding: 0px !important;
                        > button {
                            padding: 0px !important;
                            gap: 0px;
                            width: 100%;
                            justify-content: center;
                        }
                    }
                    span {
                        white-space: normal;
                    }
                    &:first-child {
                        width: 165px !important;
                        min-width: 165px !important;
                        span {
                            white-space: nowrap;
                        }
                    }
                }
            }
        `,
    },
};

const styles = tableStyles;

const InvoiceHistoryTable = (props: Props) => {
    const [selectedInvoices, setSelectedInvoices] = React.useState<SelectedInvoice[]>([]);

    React.useEffect(() => {
        return () => {
            window.dispatchEvent(new CustomEvent(selectedInvoicesChangedEventName, { detail: [] }));
        };
    }, []);

    const headerClick = (sortField: string) => {
        const sort = props.getInvoicesParameter.sort === sortField ? `${sortField} DESC` : sortField;
        props.updateSearchFields({ sort });
    };

    const sorted = (sortField: string) => {
        let sorted: boolean | string = false;
        if (props.getInvoicesParameter.sort === sortField) {
            sorted = "ascending";
        } else if (props.getInvoicesParameter.sort === `${sortField} DESC`) {
            sorted = "descending";
        }
        return sorted as SortOrderOptions;
    };

    if (props.failedToLoadInvoices) {
        return null;
    }

    const invoicesDataView = useContext(InvoicesDataViewContext);
    const toasterContext = useContext(ToasterContext);

    const setButtonAttribute = (event: any) => {
        if (event.target.tagName === "SPAN") {
            event.target.parentElement.setAttribute("disabled", "");
        } else {
            event.target.setAttribute("disabled", "");
        }
    };

    const removeButtonAttribute = (event: any) => {
        if (event.target.tagName === "SPAN") {
            event.target.parentElement.removeAttribute("disabled", "");
        } else {
            event.target.removeAttribute("disabled", "");
        }
    };
    const downloadTearsheetClickHandler = (invoiceNumber: string, event: any) => {
        if (typeof window !== undefined) {
            setButtonAttribute(event);
            props.generatePdf({
                identifier: invoiceNumber,
                templateName: "invoice",
                onSuccess(result:any) {
                    removeButtonAttribute(event);
                },
                onError(result:any) {
                    toasterContext.addToast({
                        body: translate(`${result}`),
                        messageType: "danger",
                    });
                    removeButtonAttribute(event);
                },
            });
        }
    };

    const isSuryaBrand = getCurrentDesign() === suryaDesign;
    const isMgbw = isThemeActive(THEME_IDS.MGBW);

    if (!invoicesDataView.value) {
        return (
            <MyAccountThemeWrapper>
                {!isSuryaBrand && <InvoiceHistorySuryaTitleColor />}
                <SuryaPageThemeWrapper>
                    <StyledWrapper {...styles.centeringWrapper}>
                        <LoadingSpinner {...styles.spinner}></LoadingSpinner>
                    </StyledWrapper>
                </SuryaPageThemeWrapper>
            </MyAccountThemeWrapper>
        );
    }

    if (invoicesDataView.value.length === 0) {
        return (
            <MyAccountThemeWrapper>
                {!isSuryaBrand && <InvoiceHistorySuryaTitleColor />}
                <SuryaPageThemeWrapper>
                    <StyledWrapper {...styles.noResultsContainer}>
                        <Typography {...styles.noResultsText}>{translate("No invoices found")}</Typography>
                    </StyledWrapper>
                </SuryaPageThemeWrapper>
            </MyAccountThemeWrapper>
        );
    }

    const rows = invoicesDataView.value.map(invoice => {
        return {
            id: invoice.id,
            invoiceNumber: invoice.invoiceNumber,
            date: invoice.invoiceDate
                ? new Date(invoice.invoiceDate).toLocaleDateString("en-US", {
                      month: "2-digit",
                      day: "2-digit",
                      year: "numeric",
                  })
                : "",
            terms: invoice.terms,
            dueDate: invoice.dueDate
                ? new Date(invoice.dueDate).toLocaleDateString("en-US", {
                      month: "2-digit",
                      day: "2-digit",
                      year: "numeric",
                  })
                : "",
            shipTo: `${invoice.stCompanyName} ${invoice.btAddress1} ${invoice.btAddress2} ${invoice.shipToCity} ${invoice.shipToState}`,
            customerPO: invoice.customerPO,
            status: invoice.status,
            total: invoice.invoiceTotal,
            totalDisplay: invoice.invoiceTotalDisplay,
            currentBalance: invoice.currentBalance,
            currentBalanceDisplay: invoice.currentBalanceDisplay,
            orderNumber: invoice.properties.erpOrderNo,
            brand: invoice.properties?.brand,
            isReturnSelfService: invoice.properties?.isReturnSelfService?.toLowerCase() === "true",
            isUnpaid: invoice.status?.toLowerCase() === "unpaid",
        };
    });

    const {
        showInvoiceNumber,
        showInvoiceDate,
        showTerms,
        showDueDate,
        showShipTo,
        showPONumber,
        showStatus,
        showInvoiceTotal,
        showCurrentBalance,
    } = props.fields;

    const isTrueOrUndefined = (value?: boolean) => value === true || value === undefined;

    const checkboxChangeHandler = (
        invoiceNumber: string,
        currentBalance: string | number,
        brand: string | undefined,
        value: boolean,
    ) => {
        setSelectedInvoices(selectedInvoices => {
            const nextSelectedInvoices = value
                ? selectedInvoices.some(selectedInvoice => selectedInvoice.invoiceNumber === invoiceNumber)
                    ? selectedInvoices
                    : [...selectedInvoices, { invoiceNumber, currentBalance, brand }]
                : selectedInvoices.filter(selectedInvoice => selectedInvoice.invoiceNumber !== invoiceNumber);

            window.dispatchEvent(
                new CustomEvent(selectedInvoicesChangedEventName, { detail: nextSelectedInvoices }),
            );
            return nextSelectedInvoices;
        });
    };

    return (
        <MyAccountThemeWrapper>
            {!isSuryaBrand && <InvoiceHistorySuryaTitleColor />}
            <SuryaPageThemeWrapper>
            <StyledWrapper {...styles.container}>
                <DataTable {...styles.dataTable}>
                <DataTableHead {...styles.dataTableHead}>
                    {isTrueOrUndefined(showInvoiceNumber) && (
                        <DataTableHeader
                            tight
                            sorted={sorted("invoiceNumber")}
                            {...styles.invoiceNumberHeader}
                            onSortClick={() => headerClick("invoiceNumber")}
                        >
                            {translate("Invoice #")}
                        </DataTableHeader>
                    )}
                    {isTrueOrUndefined(showInvoiceDate) && (
                        <DataTableHeader
                            tight
                            sorted={sorted("invoiceDate")}
                            {...styles.invoiceDateHeader}
                            onSortClick={() => headerClick("invoiceDate")}
                            data-test-selector="invoiceHistory_invoiceHeader_date"
                        >
                            {translate("Invoice Date")}
                        </DataTableHeader>
                    )}
                    {/*  {isTrueOrUndefined(showTerms) && (
                        <DataTableHeader
                            tight
                            sorted={sorted("terms")}
                            {...styles.termsHeader}
                            onSortClick={() => headerClick("terms")}
                        >
                            {translate("Terms")}
                        </DataTableHeader>
                    )} */}
                    {isTrueOrUndefined(showDueDate) && (
                        <DataTableHeader
                            tight
                            sorted={sorted("dueDate")}
                            {...styles.dueDateHeader}
                            onSortClick={() => headerClick("dueDate")}
                        >
                            {translate("Due Date")}
                        </DataTableHeader>
                    )}
                    {/* {isTrueOrUndefined(showShipTo) && (
                        <DataTableHeader
                            tight
                            sorted={sorted("stCompanyName")}
                            {...styles.shipToHeader}
                            onSortClick={() => headerClick("stCompanyName")}
                        >
                            {translate("Ship To / Pick Up")}
                        </DataTableHeader>
                    )} */}
                    {isTrueOrUndefined(showPONumber) && (
                        <DataTableHeader
                            tight
                            sorted={sorted("customerPO")}
                            {...styles.poNumberHeader}
                            onSortClick={() => headerClick("customerPO")}
                        >
                            {translate("PO #")}
                        </DataTableHeader>
                    )}

                    {isTrueOrUndefined(showInvoiceTotal) && props.showPricing && (
                        <DataTableHeader
                            tight
                            sorted={sorted("invoiceTotal")}
                            {...styles.totalHeader}
                            onSortClick={() => headerClick("invoiceTotal")}
                        >
                            {translate("Invoice Total")}
                        </DataTableHeader>
                    )}
                    {isTrueOrUndefined(showCurrentBalance) && props.showPricing && (
                        <DataTableHeader
                            tight
                            sorted={sorted("currentBalance")}
                            {...styles.currentBalanceHeader}
                            onSortClick={() => headerClick("currentBalance")}
                        >
                            {translate("Current Balance")}
                        </DataTableHeader>
                    )}
                    {isTrueOrUndefined(showStatus) && (
                        <DataTableHeader
                            tight
                            sorted={sorted("status")}
                            {...styles.statusHeader}
                            onSortClick={() => headerClick("status")}
                        >
                            {translate("Status")}
                        </DataTableHeader>
                    )}
                    <DataTableHeader tight className="printColumn">
                        {translate("Print")}
                    </DataTableHeader>
                    <DataTableHeader className="printColumn" tight>
                        {translate("Return")}
                    </DataTableHeader>
                </DataTableHead>
                <DataTableBody {...styles.dataTableBody}>
                    {rows.map(
                        (
                            {
                                id,
                                invoiceNumber,
                                date,
                                terms,
                                dueDate,
                                shipTo,
                                customerPO,
                                status,
                                total,
                                totalDisplay,
                                currentBalance,
                                currentBalanceDisplay,
                                orderNumber,
                                brand,
                                isReturnSelfService,
                                isUnpaid,
                            },
                            index,
                        ) => (
                            <DataTableRow
                                key={id}
                                {...styles.dataTableRow}
                                data-test-selector="invoiceHistory_invoiceLine"
                                data-test-key={id}
                            >
                                {isTrueOrUndefined(showInvoiceNumber) && (
                                    <DataTableCell
                                        {...styles.invoiceNumberCell}
                                        data-test-selector="invoiceHistory_invoiceLine_number"
                                    >
                                        <StyledWrapper {...styles.invoiceNumberWrapper}>
                                            <StyledWrapper {...styles.invoiceCheckboxSlot}>
                                                {isUnpaid && (
                                                    <Checkbox
                                                        {...styles.unpaidInvoiceCheckbox}
                                                        checked={selectedInvoices.some(
                                                            selectedInvoice =>
                                                                selectedInvoice.invoiceNumber === invoiceNumber,
                                                        )}
                                                        onChange={(event, value) =>
                                                            checkboxChangeHandler(
                                                                invoiceNumber,
                                                                currentBalance,
                                                                brand,
                                                                value,
                                                            )
                                                        }
                                                        data-test-selector="invoiceHistory_unpaidInvoiceCheckbox"
                                                        hiddenIconText={translate("Select invoice")}
                                                    />
                                                )}
                                            </StyledWrapper>
                                            <InvoiceDetailPageTypeLink
                                                title={invoiceNumber}
                                                invoiceNumber={invoiceNumber}
                                            />
                                        </StyledWrapper>
                                    </DataTableCell>
                                )}
                                {isTrueOrUndefined(showInvoiceDate) && (
                                    <DataTableCell
                                        {...styles.invoiceDateCell}
                                        data-test-selector="invoiceHistory_invoiceLine_date"
                                    >
                                        {date}
                                    </DataTableCell>
                                )}
                                {/* {isTrueOrUndefined(showTerms) && (
                                    <DataTableCell {...styles.termsCell}>{terms}</DataTableCell>
                                )} */}
                                {isTrueOrUndefined(showDueDate) && (
                                    <DataTableCell {...styles.dueDateCell}>{dueDate}</DataTableCell>
                                )}
                                {/* {isTrueOrUndefined(showShipTo) && (
                                    <DataTableCell {...styles.shipToCell}>{shipTo}</DataTableCell>
                                )} */}
                                {isTrueOrUndefined(showPONumber) && (
                                    <DataTableCell {...styles.poNumberCell}>{customerPO}</DataTableCell>
                                )}

                                {isTrueOrUndefined(showInvoiceTotal) && props.showPricing && (
                                    <DataTableCell {...styles.totalCell}>
                                        {String(total).includes("-")
                                            ? `$(${Math.abs(total)
                                                .toFixed(2) 
                                                .replace(/\B(?=(\d{3})+(?!\d))/g, ",")})`
                                            : totalDisplay}
                                    </DataTableCell>
                                )}
                                {isTrueOrUndefined(showCurrentBalance) && props.showPricing && (
                                    <DataTableCell {...styles.currentBalanceCell}>
                                        {String(currentBalance).includes("-")
                                            ? `$(${Math.abs(currentBalance)
                                                .toFixed(2) 
                                                .replace(/\B(?=(\d{3})+(?!\d))/g, ",")})`
                                            : currentBalanceDisplay}
                                    </DataTableCell>
                                )}
                                {isTrueOrUndefined(showStatus) && (
                                    <DataTableCell {...styles.statusCell}>{status}</DataTableCell>
                                )}
                                <DataTableCell className="printColumn">
                                    <Button
                                        {...styles.button}
                                        onClick={event => downloadTearsheetClickHandler(invoiceNumber, event)}
                                    >
                                        <StyledWrapper>
                                            <Icon src={Printer} {...styles.printIcon} />
                                        </StyledWrapper>
                                    </Button>
                                </DataTableCell>

                                <DataTableCell {...styles.returnorderStyles} className="printColumn">
                                    {isMgbwInvoiceBrand(brand, isMgbw) ? (
                                        isReturnSelfService ? (
                                            <Clickable
                                                href={`${props.rmaLink?.url}?orderNumber=${orderNumber}&invoiceNumber=${invoiceNumber}&invoiceDate=${date}&PoNumber=${customerPO}`}
                                            >
                                                <Icon src={CustomReturnIcon} {...styles.returnIcon} />
                                            </Clickable>
                                        ) : (
                                            <Typography as="div" {...styles.circleIcon}>
                                                <QuestionCircle size={16} src={ProductAvailabilityInfoIcon} />{" "}
                                                <Typography as="span" {...styles.disableTooltipMessage}>
                                                    {siteMessage("MGBW_returns_contactSupport").toString()}
                                                </Typography>{" "}
                                            </Typography>
                                        )
                                    ) : (
                                        <Clickable
                                            href={`${props.rmaLink?.url}?orderNumber=${orderNumber}&invoiceNumber=${invoiceNumber}&invoiceDate=${date}&PoNumber=${customerPO}`}
                                        >
                                            <Icon src={CustomReturnIcon} {...styles.returnIcon} />
                                        </Clickable>
                                    )}
                                </DataTableCell>
                            </DataTableRow>
                        ),
                    )}
                </DataTableBody>
            </DataTable>
        </StyledWrapper>
            </SuryaPageThemeWrapper>
        </MyAccountThemeWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(InvoiceHistoryTable),
    definition: {
        group: "Invoice History",
        displayName: "Search Results Table",
        allowedContexts: ["InvoiceHistoryPage"],
        fieldDefinitions: [
            {
                name: fields.showInvoiceNumber,
                displayName: "Invoice #",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                isEnabled: () => false,
                fieldType: "General",
                sortOrder: 0,
            },
            {
                name: fields.showInvoiceDate,
                displayName: "Invoice Date",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 1,
            },
            {
                name: fields.showTerms,
                displayName: "Terms",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 2,
            },
            {
                name: fields.showDueDate,
                displayName: "Due Date",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 3,
            },
            {
                name: fields.showShipTo,
                displayName: "Ship To/Pick Up",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 4,
            },
            {
                name: fields.showPONumber,
                displayName: "PO #",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 5,
            },
            {
                name: fields.showStatus,
                displayName: "Status",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 6,
            },
            {
                name: fields.showInvoiceTotal,
                displayName: "Invoice Total",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 7,
            },
            {
                name: fields.showCurrentBalance,
                displayName: "Current Balance",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 8,
            },
        ],
    },
};

export default widgetModule;